import React, { useContext, useState } from 'react';

import cx from 'classnames';
import { always, map, pipe, prop, reject, unless } from 'ramda';

import Icon from 'components/shared/Icon';
import Burger from 'components/shared/Icon/icons/Burger';
import CloseBurger from 'components/shared/Icon/icons/CloseBurger';
import SAFARI_PORTAL_LOGO from 'components/shared/images/safari_portal.svg';
import LandingPageContext from 'components/shared/LandingPage/contexts/LandingPageContext';
import Tooltip from 'components/shared/Tooltip';
import Text from 'components/shared/Typography/Text';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';

import { LANDING_THEMES } from '../../../../../../utils/itineraries/functions';
import MobileNav from '../../../../../Layouts/Form/FormBuilder/MobileNav';
import { Navigation } from '../../../LeftSide';
import { ActionButton } from '../../PageFooter/shared';
import { iconsConfig } from '../constants';
import styles from '../index.module.css';
import { IIcon, IPageHeaderProps, IIconConfig, IBurgerMenuProps } from '../types';
import { Wrapper } from '../Wrapper';

const PROPOSALS_KEYWORDS = ['itineraries', 'itinerary', 'lookbook'];

function InlineBuilderItinerary(props: IPageHeaderProps) {
  const { links, disabled = false, hideBurgerMenu } = props;
  const { headerOptions, landingTheme, actionButtonOptions, private: isPrivate } = useContext(LandingPageContext);
  const { logoSrc, logoCustomStyle, icons, iconsParams = {}, companyName } = headerOptions;
  const { isTabletOrMobile } = useMediaBreakpoints();
  const currentLocation = window.location.href;
  const isAgentProperty = currentLocation.includes('type=agent&acid=');
  const isSupplierProperty = currentLocation.includes('type=owner&acid=');
  const isProposal = PROPOSALS_KEYWORDS.some((k) => currentLocation.includes(k));
  const isDefaultTheme = landingTheme === LANDING_THEMES.default;
  const isBoatsTheme = landingTheme === LANDING_THEMES.boats;
  const hasBurgerMenu = !isTabletOrMobile && (isDefaultTheme || isBoatsTheme) && !hideBurgerMenu;

  const isActionButtonVisible = !isPrivate && !isTabletOrMobile;

  const renderIcon = (icon: IIcon) => {
    const iconConfig: IIconConfig = iconsConfig[icon.type];

    if (!iconConfig || icon.type === 'help') return null;

    const containerClasses = cx(
      styles.iconWrapper,
      iconsParams.margin === 'mini' && styles.iconWrapperMarginMini,
      'custom-right-header-icon-wrapper',
      { [styles.customIconWrapper]: !isDefaultTheme },
    );

    const iconClasses = cx(
      disabled && styles.notAllowedCursor,
      styles.icon,
      iconConfig.className,
      iconConfig.classNamePortal,
      'custom-right-header-icon',
      `custom-right-header-icon-${icon.type}`,
    );

    return (
      <div key={icon.type} className={containerClasses} onClick={unless(always(disabled), icon.onClick)}>
        <Icon
          component={iconConfig.icon}
          className={iconClasses}
          data-tip
          data-for={`right_header_icon_${icon.type}`}
        />

        <Tooltip id={`right_header_icon_${icon.type}`} disable={isAgentProperty}>
          Active on live itinerary link when shared.
        </Tooltip>
      </div>
    );
  };

  // @ts-ignore
  const renderIcons = props.renderIcons || pipe(reject(prop('hidden')), map(renderIcon));

  const renderLogo = () => {
    if (!logoSrc && companyName) {
      return (
        <Text hoefler italic className={cx(styles.companyName, 'custom-right-header-companyname')}>
          {companyName}
        </Text>
      );
    }

    return (
      <img
        src={logoSrc || SAFARI_PORTAL_LOGO}
        alt="logo"
        style={logoCustomStyle}
        className="custom-right-header-logo"
        crossOrigin=""
      />
    );
  };

  return (
    <>
      <Wrapper links={links}>
        <div className={cx(styles.header, 'custom-right-header')} id="landingHeader">
          {hasBurgerMenu && (
            <BurgerMenu links={links} disabled isAgentProperty={isAgentProperty || isSupplierProperty || !isProposal} />
          )}
          <div
            className={cx(
              !isDefaultTheme ? styles.customHeaderLogo : styles.headerLogo,
              !isDefaultTheme && styles.centerLogo,
              !isProposal && !isAgentProperty && styles.centerLogo,
              isAgentProperty && !isTabletOrMobile && styles.renderLogo,
              disabled && styles.disablePointerEvent,
              'custom-right-header-logo',
            )}
          >
            {renderLogo()}
          </div>
          {!isTabletOrMobile && (
            <div
              className={cx(
                styles.headerActions,
                !isDefaultTheme && styles.notDefaultThemeActions,
                isSupplierProperty && styles.supplierActions,
                'custom-right-header-actions',
              )}
            >
              {/*@ts-ignore*/}
              {renderIcons(icons)}
              {isActionButtonVisible && <ActionButton {...actionButtonOptions} />}
            </div>
          )}
        </div>
        {/*@ts-ignore*/}
        {!isTabletOrMobile && !isDefaultTheme && !isBoatsTheme && <MobileNav links={links} navigation id="mobileNav" />}
      </Wrapper>
    </>
  );
}

export default InlineBuilderItinerary;

export const BurgerMenu = ({
  links,
  width,
  height,
  classname,
  disabled = false,
  isAgentProperty = false,
}: IBurgerMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isTabletOrMobile } = useMediaBreakpoints();

  const openBurgerMenu = () => {
    if (disabled) {
      return null;
    }
    setIsOpen(!isOpen);
  };

  return (
    <>
      <div
        className={cx(
          styles.headerBurgerContainer,
          isAgentProperty && styles.hideBurgerContainer,
          disabled && styles.notAllowedCursor,
        )}
      >
        <div
          data-tip
          data-for={'right_header_icon_burger'}
          style={{ display: 'flex' }}
          className="header_menu_icon_wrapper"
          onClick={openBurgerMenu}
        >
          {isOpen && !isTabletOrMobile ? <CloseBurger /> : <Burger width={width} height={height} />}
        </div>
        {/*@ts-ignore*/}
        {!disabled && <Navigation isOpen={isOpen} links={links} setIsOpen={setIsOpen} classname={classname} />}
      </div>
      {/*@ts-ignore*/}
      {disabled && <Tooltip id={'right_header_icon_burger'}>Active on live itinerary link when shared.</Tooltip>}
    </>
  );
};
